import React, { useState } from "react";
import adminTemplatesService, {
  AdminTemplate,
  TemplateSection,
  SPECIALTY_CHOICES,
} from "../../../services/adminTemplatesService";
import TemplateBuilder, {
  BuilderNode,
  builderToConfigSections,
  configToBuilder,
} from "./TemplateBuilder";

interface TemplateFormModalProps {
  template: AdminTemplate | null; // null = create
  onClose: () => void;
  onSaved: (template: AdminTemplate) => void;
}

const DEFAULT_SYSTEM_PROMPT =
  "You are an expert medical scribe. Extract each section faithfully from the " +
  "transcript; never invent facts. Leave a section empty if the transcript " +
  "contains no information for it.";

// SOAP starter structure (matches the classic SOAP hierarchy)
const SOAP_STARTER_SECTIONS: TemplateSection[] = [
  {
    key: "subjective",
    title: "Subjective",
    sections: [
      { key: "chief_complaint", title: "Chief Complaint", type: "string" },
      { key: "hpi", title: "History of Present Illness", type: "string" },
      {
        key: "ros",
        title: "Review of Systems",
        sections: [
          { key: "general", title: "General", type: "string" },
          { key: "cardiovascular", title: "Cardiovascular", type: "string" },
          { key: "respiratory", title: "Respiratory", type: "string" },
        ],
      },
    ],
  },
  {
    key: "objective",
    title: "Objective",
    sections: [
      { key: "vital_signs", title: "Vital Signs", type: "string" },
      {
        key: "physical_examination",
        title: "Physical Examination",
        sections: [
          { key: "heent", title: "HEENT", type: "string" },
          { key: "neck", title: "Neck", type: "string" },
          { key: "cardiovascular", title: "Cardiovascular", type: "string" },
        ],
      },
    ],
  },
  {
    key: "assessment",
    title: "Assessment",
    sections: [
      { key: "primary_diagnosis", title: "Primary Diagnosis", type: "string" },
      { key: "differential_diagnosis", title: "Differential Diagnosis", type: "string" },
    ],
  },
  {
    key: "plan",
    title: "Plan",
    sections: [
      { key: "medications", title: "Medications", type: "array" },
      { key: "follow_up", title: "Follow-up", type: "string" },
    ],
  },
];

const TemplateFormModal: React.FC<TemplateFormModalProps> = ({
  template,
  onClose,
  onSaved,
}) => {
  const [name, setName] = useState(template?.name || "");
  const [documentationType, setDocumentationType] = useState(
    template?.documentation_type || "custom",
  );
  const [description, setDescription] = useState(template?.description || "");
  const [active, setActive] = useState(template?.active ?? true);
  const [specialties, setSpecialties] = useState<string[]>(template?.specialties || []);
  const [systemPrompt, setSystemPrompt] = useState(
    template?.json_config?.system_prompt || DEFAULT_SYSTEM_PROMPT,
  );
  const [nodes, setNodes] = useState<BuilderNode[]>(
    configToBuilder(template?.json_config?.sections || []),
  );

  const [mode, setMode] = useState<"visual" | "json">("visual");
  const [jsonText, setJsonText] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const assembleConfig = () => ({
    name: name.trim(),
    documentation_type: documentationType.trim(),
    description: description.trim(),
    system_prompt: systemPrompt,
    sections: builderToConfigSections(nodes),
  });

  // Switch visual → advanced JSON: serialize the current config
  const openJsonMode = () => {
    setError(null);
    setJsonText(JSON.stringify(assembleConfig(), null, 2));
    setMode("json");
  };

  // Switch advanced JSON → visual: parse back into the builder + fields
  const openVisualMode = () => {
    try {
      const cfg = JSON.parse(jsonText);
      if (cfg.system_prompt !== undefined) setSystemPrompt(cfg.system_prompt);
      if (cfg.documentation_type) setDocumentationType(cfg.documentation_type);
      if (cfg.description !== undefined) setDescription(cfg.description);
      setNodes(configToBuilder(cfg.sections || []));
      setError(null);
      setMode("visual");
    } catch (e: any) {
      setError(`Invalid JSON: ${e.message}`);
    }
  };

  const toggleSpecialty = (value: string) => {
    setSpecialties((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    );
  };

  const loadSoapStarter = () => {
    if (
      nodes.length > 0 &&
      !window.confirm("Replace the current sections with the SOAP starter?")
    )
      return;
    setNodes(configToBuilder(SOAP_STARTER_SECTIONS));
  };

  const handleSave = async () => {
    setError(null);

    let jsonConfig: any;
    if (mode === "json") {
      try {
        jsonConfig = JSON.parse(jsonText);
      } catch (e: any) {
        setError(`Invalid JSON: ${e.message}`);
        return;
      }
    } else {
      jsonConfig = assembleConfig();
      if (!jsonConfig.sections.length) {
        setError("Add at least one section.");
        return;
      }
    }

    setSaving(true);
    try {
      const payload = {
        name,
        documentation_type: documentationType,
        description,
        active,
        specialties,
        json_config: jsonConfig,
      };
      const saved = template
        ? await adminTemplatesService.update(template.id, payload)
        : await adminTemplatesService.create(payload);
      onSaved(saved);
    } catch (err: any) {
      const data = err?.response?.data;
      const msg =
        data?.json_config?.[0] ||
        data?.name?.[0] ||
        data?.detail ||
        err.message ||
        "Save failed";
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/60 backdrop-blur-sm p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col max-h-[92vh] overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-bold text-gray-900">
            {template ? "Edit Template" : "New Template"}
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition"
          >
            ✕
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                Name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                placeholder="Standard SOAP Note"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                Documentation Type
              </label>
              <input
                type="text"
                value={documentationType}
                onChange={(e) => setDocumentationType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                placeholder="SOAP, H&P, Consultation Note..."
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
              Description
            </label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="Short admin-facing note"
            />
          </div>

          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={active}
              onChange={(e) => setActive(e.target.checked)}
              className="rounded border-gray-300"
            />
            Active (visible to doctors who can see it)
          </label>

          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
              Specialties
            </label>
            <p className="text-xs text-gray-400 mb-2">
              Visible to every doctor with a matching specialty, in addition to
              anyone individually assigned via "Assign". Leave empty for
              individual-assignment only.
            </p>
            <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-3 grid grid-cols-2 gap-x-4 gap-y-1.5">
              {SPECIALTY_CHOICES.map((opt) => (
                <label
                  key={opt.value}
                  className="flex items-center gap-2 text-sm text-gray-700"
                >
                  <input
                    type="checkbox"
                    checked={specialties.includes(opt.value)}
                    onChange={() => toggleSpecialty(opt.value)}
                    className="rounded border-gray-300"
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
              AI Instructions
            </label>
            <textarea
              value={systemPrompt}
              onChange={(e) => setSystemPrompt(e.target.value)}
              rows={3}
              className="w-full text-sm px-3 py-2 border border-gray-200 rounded-lg
                focus:outline-none focus:ring-2 focus:ring-blue-400 resize-y leading-relaxed"
              placeholder="How the AI should fill in this document..."
            />
            <p className="text-xs text-gray-400 mt-1">
              Tells the AI how to extract content from the transcript.
            </p>
          </div>

          {/* Document structure */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-xs font-semibold text-gray-500 uppercase">
                Document Structure
              </label>
              <div className="flex items-center gap-3">
                {mode === "visual" && (
                  <button
                    type="button"
                    onClick={loadSoapStarter}
                    className="text-xs text-gray-500 hover:text-gray-800 font-medium"
                  >
                    Start from SOAP
                  </button>
                )}
                <button
                  type="button"
                  onClick={mode === "visual" ? openJsonMode : openVisualMode}
                  className="text-xs text-blue-600 hover:text-blue-800 font-semibold"
                >
                  {mode === "visual" ? "Advanced (JSON)" : "Visual builder"}
                </button>
              </div>
            </div>

            {mode === "visual" ? (
              <>
                <TemplateBuilder nodes={nodes} onChange={setNodes} />
                <p className="text-xs text-gray-400 mt-1.5">
                  📁 folders group related fields; 📄 fields are filled by the AI.
                  Use “Text” for a paragraph or “List” for bullet points. Nest as
                  deep as you like.
                </p>
              </>
            ) : (
              <>
                <textarea
                  value={jsonText}
                  onChange={(e) => setJsonText(e.target.value)}
                  rows={16}
                  spellCheck={false}
                  className="w-full font-mono text-xs px-3 py-2 border border-gray-200 rounded-lg
                    focus:outline-none focus:ring-2 focus:ring-blue-400 resize-y leading-relaxed"
                />
                <p className="text-xs text-gray-400 mt-1">
                  Advanced: a node has either <code>sections</code> (a group) or{" "}
                  <code>type</code> (a leaf: "string" | "array").
                </p>
              </>
            )}
          </div>

          {error && (
            <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {error}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 text-sm font-semibold text-gray-500 hover:text-gray-900 transition"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !name.trim()}
            className="px-5 py-2 text-sm font-semibold text-white bg-gray-900 hover:bg-black
              rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition"
          >
            {saving ? "Saving..." : "Save Template"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TemplateFormModal;
